import React, { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyPurchases, PurchasesResponse } from '@/lib/collections/purchases';
import { subscribeManyWallpapers, WallpapersResponse } from '@/lib/collections/wallpapers';
import { subscribeManyUserProfiles, UserProfilesResponse } from '@/lib/collections/userProfiles';
import { ADMIN_ADDRESS } from '@/lib/constants';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SOLANA_EXPLORER = 'https://solscan.io/tx/';

// Creator receives 70% of each sale. When the creator is also the admin they
// also collect the 10% admin fee, making their effective share 80%.
function getCreatorShare(creatorAddress: string): number {
  return creatorAddress === ADMIN_ADDRESS ? 0.8 : 0.7;
}

export function formatSkr(raw: number): string {
  const val = raw / 1_000_000;
  return val % 1 === 0 ? val.toFixed(0) : val.toFixed(2);
}

export function formatDate(ts: number): string {
  const d = new Date(ts * 1000);
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

export function formatHour(ts: number): string {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  const s = d.getSeconds().toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
}

export function shortenAddress(addr: string, chars = 4): string {
  if (addr.length < chars * 2 + 2) return addr;
  return `${addr.slice(0, chars)}…${addr.slice(-chars)}`;
}

// ─── Per-row component ────────────────────────────────────────────────────────

interface TransactionRowProps {
  purchase: PurchasesResponse;
  wallpaper: WallpapersResponse | undefined;
  isLast: boolean;
  profileMap: Record<string, string>;
  showExternalLinks: boolean;
  onWallpaperClick?: (wallpaperId: string) => void;
}

const TransactionRow: React.FC<TransactionRowProps> = ({
  purchase,
  wallpaper,
  isLast,
  profileMap,
  showExternalLinks,
  onWallpaperClick,
}) => {
  const buyer = purchase.buyer ?? '';
  const seller = purchase.creator ?? '';

  // Apply creator share before formatting to avoid rounding on the raw integer amount.
  // Admin creators receive 80% (70% creator + 10% admin fee); others receive 70%.
  const share = getCreatorShare(seller);
  const skr = formatSkr(Math.floor(purchase.amt * share));
  const ts = purchase.tarobase_created_at || (purchase.createdAt && !isNaN(purchase.createdAt) ? purchase.createdAt : 0);
  const date = ts ? formatDate(ts) : '—';
  const hour = ts ? formatHour(ts) : '';
  const txLink = `${SOLANA_EXPLORER}${purchase.id}`;

  const buyerLabel = buyer
    ? (profileMap[buyer] ? `@${profileMap[buyer]}` : shortenAddress(buyer))
    : '—';

  const title = wallpaper?.title ?? shortenAddress(purchase.wallpaperId, 6);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '10px 20px',
        borderBottom: !isLast ? '1px solid hsl(246, 24%, 14%)' : 'none',
        fontFamily: "'DM Mono', monospace",
        color: 'hsl(240, 20%, 96%)',
        letterSpacing: '0.03em',
        overflow: 'hidden',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = 'hsl(246, 28%, 14%)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      {/* Line 1: wallpaper name + price */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 0 }}>
        {showExternalLinks || !onWallpaperClick ? (
          <span style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 12,
            paddingRight: 16,
          }}>
            {title}
          </span>
        ) : (
          <button
            onClick={() => onWallpaperClick(purchase.wallpaperId)}
            style={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 12,
              paddingRight: 16,
              background: 'none',
              border: 'none',
              padding: '0 16px 0 0',
              cursor: 'pointer',
              color: 'hsl(262, 83%, 72%)',
              textAlign: 'left',
              fontFamily: "'DM Mono', monospace",
              letterSpacing: '0.03em',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.7')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
          >
            {title}
          </button>
        )}
        <span style={{
          flexShrink: 0,
          color: 'hsl(262, 83%, 66%)',
          fontWeight: 500,
          fontSize: 12,
        }}>
          {skr} SKR earned
        </span>
      </div>

      {/* Line 2: date+hour */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{
          color: 'hsl(246, 16%, 55%)',
          fontSize: 11,
        }}>
          {date} · {hour}
        </span>
      </div>

      {/* Line 3: buyer from seller + TX link or plain tx id */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          color: 'hsl(240, 60%, 75%)',
          fontSize: 11,
          fontFamily: "'DM Mono', monospace",
          letterSpacing: '0.04em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0,
        }}>
          <span style={{ color: 'hsl(246, 16%, 45%)', marginRight: 4 }}>received from</span>
          <span style={{ color: 'hsl(262, 83%, 72%)' }}>{buyerLabel}</span>
        </span>

        {showExternalLinks ? (
          <a
            href={txLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: 'hsl(262, 83%, 66%)',
              fontSize: 10,
              fontFamily: "'DM Mono', monospace",
              textDecoration: 'none',
              transition: 'opacity 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.7')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
          >
            <ExternalLink size={10} />
            {shortenAddress(purchase.id, 6)}
          </a>
        ) : (
          <span style={{
            color: 'hsl(246, 16%, 40%)',
            fontSize: 10,
            fontFamily: "'DM Mono', monospace",
            letterSpacing: '0.04em',
            flexShrink: 0,
          }}>
            {shortenAddress(purchase.id, 6)}
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Main TransactionsList component ─────────────────────────────────────────

export interface TransactionsListProps {
  /**
   * When provided, filters to purchases where creator === creatorAddress.
   * When omitted, shows all purchases (admin view).
   */
  creatorAddress?: string;
  /** Whether to show external Solscan links. Pass false for profile view. */
  showExternalLinks: boolean;
  /** Called when user clicks a wallpaper title (profile view only, no external links). */
  onWallpaperClick?: (wallpaperId: string) => void;
  /** Whether data should be subscribed. Defaults to true. */
  enabled?: boolean;
}

export const TransactionsList: React.FC<TransactionsListProps> = ({
  creatorAddress,
  showExternalLinks,
  onWallpaperClick,
  enabled = true,
}) => {
  const filter = creatorAddress
    ? `where creator = '${creatorAddress}'`
    : '';

  const { data: purchases, loading } = useRealtimeData<PurchasesResponse[]>(
    subscribeManyPurchases,
    enabled,
    filter,
  );

  const { data: wallpapers } = useRealtimeData<WallpapersResponse[]>(
    subscribeManyWallpapers,
    enabled,
  );

  const { data: userProfiles } = useRealtimeData<UserProfilesResponse[]>(
    subscribeManyUserProfiles,
    enabled,
  );

  const wallpaperMap = useMemo<Record<string, WallpapersResponse>>(() => {
    if (!wallpapers) return {};
    return wallpapers.reduce<Record<string, WallpapersResponse>>((acc, w) => {
      acc[w.id] = w;
      return acc;
    }, {});
  }, [wallpapers]);

  const profileMap = useMemo<Record<string, string>>(() => {
    if (!userProfiles) return {};
    return userProfiles.reduce<Record<string, string>>((acc, p) => {
      if (p.id && p.username) acc[p.id] = p.username;
      return acc;
    }, {});
  }, [userProfiles]);

  const sorted = useMemo<PurchasesResponse[]>(() => {
    if (!purchases) return [];
    const getTs = (p: PurchasesResponse) =>
      p.tarobase_created_at || (p.createdAt && !isNaN(p.createdAt) ? p.createdAt : 0);
    return [...purchases].sort((a, b) => getTs(b) - getTs(a));
  }, [purchases]);

  const emptyMessage = creatorAddress ? 'NO SALES YET' : 'NO TRANSACTIONS YET';

  return (
    <div style={{ padding: '4px 0' }}>
      {loading && (
        <div style={{
          padding: '40px 18px',
          textAlign: 'center',
          color: 'hsl(246, 16%, 55%)',
          fontSize: 12,
          fontFamily: "'DM Mono', monospace",
          letterSpacing: '0.08em',
        }}>
          LOADING...
        </div>
      )}

      {!loading && sorted.length === 0 && (
        <div style={{
          padding: '40px 18px',
          textAlign: 'center',
          color: 'hsl(246, 16%, 55%)',
          fontSize: 12,
          fontFamily: "'DM Mono', monospace",
          letterSpacing: '0.08em',
        }}>
          {emptyMessage}
        </div>
      )}

      {!loading && sorted.map((purchase, i) => (
        <TransactionRow
          key={purchase.id}
          purchase={purchase}
          wallpaper={wallpaperMap[purchase.wallpaperId]}
          isLast={i === sorted.length - 1}
          profileMap={profileMap}
          showExternalLinks={showExternalLinks}
          onWallpaperClick={onWallpaperClick}
        />
      ))}

      {!loading && sorted.length > 0 && (
        <div style={{
          borderTop: '1px solid hsl(246, 24%, 14%)',
          padding: '9px 20px',
          textAlign: 'right',
          fontSize: 11,
          fontFamily: "'DM Mono', monospace",
          color: 'hsl(246, 16%, 55%)',
          letterSpacing: '0.08em',
        }}>
          {sorted.length} RECORD{sorted.length !== 1 ? 'S' : ''}
        </div>
      )}
    </div>
  );
};

export default TransactionsList;
